"use client";

import clsx from "clsx";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import { ImageViewer } from "./_components/image-viewer";
import { useHorizontalDragGuard } from "./_hooks/use-horizontal-drag-guard";
import {
  useIndicatorVisibility,
  type IndicatorTransition,
} from "./_hooks/use-indicator-visibility";

type Props = {
  images: { url: string }[];
  altText: string;
  /** 先頭画像を priority (eager) で読むか。一覧では above-the-fold の 1 件のみ true にする */
  heroPriority?: boolean;
  /** 画像タップ時に ImageViewer の代わりに呼ぶハンドラ */
  onImageClick?: () => void;
};

const LG_MIN_WIDTH = 1024;

const TRANSITION_CLASS: Record<IndicatorTransition, string> = {
  fadeIn: "duration-500 ease-[cubic-bezier(0.33,0,0.2,1)]",
  swipeFadeIn: "duration-200 ease-out",
  fadeOut: "duration-800 ease-[cubic-bezier(0.37,0,0.63,1)]",
  scrollHide: "duration-150 ease-linear",
};

/**
 * MainVisual のサムネイル列の代わりに、写真上へ枚数分のラインを重ねて現在位置を示すカルーセル。
 * ラインは操作・スクロール停止などのきっかけがあった時だけ表示し、しばらくすると消える。
 */
export function MainVisualWithIndicator({
  images,
  altText,
  heroPriority = true,
  onImageClick,
}: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    dragFree: false,
    containScroll: "trimSnaps",
    watchDrag: true,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  // 1 枚以下では位置を示す意味が無いため、バーも表示制御も持たない
  const hasIndicator = images.length > 1;
  const visibility = useIndicatorVisibility(emblaApi, hasIndicator);
  const dragGuard = useHorizontalDragGuard();

  useEffect(() => {
    if (!emblaApi) return;
    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on("select", handleSelect);
    emblaApi.on("reInit", handleSelect);
    return () => {
      emblaApi.off("select", handleSelect);
      emblaApi.off("reInit", handleSelect);
    };
  }, [emblaApi]);

  const openModalIfMobile = useCallback(() => {
    if (window.innerWidth >= LG_MIN_WIDTH) return;
    setModalOpen(true);
  }, []);

  const handleImageClick = onImageClick ?? openModalIfMobile;
  const currentImage = images[currentIndex];

  return (
    <>
      <div className="relative">
        <div
          ref={emblaRef}
          className="bg-dx-surface-subtle aspect-[3/2] w-full overflow-hidden"
          {...dragGuard}
        >
          <div className="flex h-full">
            {images.map((image, i) => (
              <div
                // 画像の表示切り替えだけで、行動の分析対象にならない
                data-analytics-ignore
                key={i}
                className="relative h-full w-full shrink-0 grow-0 basis-full cursor-zoom-in select-none lg:cursor-default"
                onClick={handleImageClick}
                role={onImageClick ? undefined : "button"}
                tabIndex={-1}
                aria-label={onImageClick ? undefined : "画像を拡大表示"}
                // onImageClick 時は拠点名見出しの button と開く対象・アクセシブルネームが重複するため隠す。
                aria-hidden={onImageClick ? true : undefined}
              >
                <Image
                  src={image.url}
                  alt={i === 0 ? altText : ""}
                  fill
                  className="pointer-events-none object-cover object-center"
                  sizes="(min-width: 640px) 640px, 100vw"
                  draggable={false}
                  priority={heroPriority && i === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {hasIndicator && (
          <div
            data-testid="main-visual-indicator"
            aria-hidden
            className={clsx(
              "to-dx-base-black/35 pointer-events-none absolute inset-x-0 bottom-0 h-[90px] bg-linear-to-b from-transparent transition-opacity",
              visibility.visible ? "opacity-100" : "opacity-0",
              TRANSITION_CLASS[visibility.transition],
            )}
          >
            <div className="absolute inset-x-[16px] bottom-[16px] flex gap-[3px]">
              {images.map((_, i) => (
                <span
                  key={i}
                  data-testid="main-visual-indicator-line"
                  className={clsx(
                    "h-[3px] flex-1 rounded-[2px] transition-colors duration-200",
                    i === currentIndex
                      ? "bg-dx-base-white"
                      : "bg-dx-base-white/40",
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {modalOpen && currentImage && (
        <ImageViewer
          imageUrl={currentImage.url}
          altText={`${altText}（${currentIndex + 1}枚目）`}
          caption={null}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
